// src/utils/lazyWithRetry.ts
// Wraps React.lazy with automatic retry on ChunkLoadError.
// When webpack chunks fail to load (e.g., offline, dev server recompile),
// this retries up to 3 times before giving up.

import React from 'react';

type ComponentModule = { default: React.ComponentType<any> };

export function lazyWithRetry(
  importFn: () => Promise<ComponentModule>,
  retries = 3,
  delay = 1000
): React.LazyExoticComponent<React.ComponentType<any>> {
  return React.lazy(() => retry(importFn, retries, delay));
}

function retry(
  importFn: () => Promise<ComponentModule>,
  retries: number,
  delay: number
): Promise<ComponentModule> {
  return importFn().catch((error: Error) => {
    if (retries <= 0) {
      throw error;
    }
    // Only retry on chunk load errors
    if (
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch')
    ) {
      return new Promise<ComponentModule>((resolve) => {
        setTimeout(() => {
          resolve(retry(importFn, retries - 1, delay));
        }, delay);
      });
    }
    throw error;
  });
}

export default lazyWithRetry;
